/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Advanced management interface for Cook units with production analytics.
 */

import React, { useState } from "react";
import {
  FlaskConical,
  Filter,
  ArrowUpDown,
  Zap,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const RARITY_COLORS = {
  common: "border-zinc-800 text-zinc-400 bg-zinc-900/50",
  rare: "border-blue-600 text-blue-400 bg-blue-900/10",
  epic: "border-purple-600 text-purple-400 bg-purple-900/10",
  legendary:
    "border-yellow-500 text-yellow-400 bg-yellow-900/10 shadow-[0_0_15px_rgba(234,179,8,0.1)]",
};

export default function CooksManagement({ inventory, onBack }) {
  const [rarityFilter, setRarityFilter] = useState("all");
  const [sortBy, setSortBy] = useState("yield_desc");

  const cooks = inventory.filter((i) => i.type === "cook");

  // Calculate total potential yield per tick
  const totalYield = cooks.reduce(
    (acc, c) => acc + c.production_rate * c.quantity,
    0
  );

  const filteredCooks = cooks
    .filter((c) => rarityFilter === "all" || c.rarity === rarityFilter)
    .sort((a, b) => {
      if (sortBy === "yield_desc") return b.production_rate - a.production_rate;
      if (sortBy === "yield_asc") return a.production_rate - b.production_rate;
      if (sortBy === "qty") return b.quantity - a.quantity;
      return 0;
    });

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-blue-900 pb-4">
        <div>
          <h2 className="text-3xl font-black italic text-blue-500 uppercase flex items-center gap-2">
            <FlaskConical size={28} /> Lab Operations
          </h2>
          <p className="text-xs text-blue-400 font-mono uppercase tracking-widest">
            Chemical Production Personnel
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-6 py-2 bg-zinc-900 border border-zinc-700 text-zinc-300 hover:text-white hover:border-white transition-all uppercase font-bold text-xs"
        >
          &larr; Back to Trap
        </button>
      </div>

      {/* Analytics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-black border border-blue-900/50 p-4 relative overflow-hidden group">
          <div className="relative z-10 flex justify-between items-start">
            <div>
              <p className="text-[10px] text-blue-700 uppercase font-bold flex items-center gap-1">
                <FlaskConical size={12} /> Active Chemists
              </p>
              <p className="text-2xl font-black text-white">
                {cooks.reduce((acc, c) => acc + c.quantity, 0).toLocaleString()}
              </p>
            </div>
          </div>
          <FlaskConical
            className="absolute -bottom-4 -right-4 text-blue-900/20 group-hover:text-blue-900/30 transition-all transform -rotate-12"
            size={80}
          />
        </div>

        <div className="bg-black border border-green-900/50 p-4 relative overflow-hidden group">
          <div className="relative z-10 flex justify-between items-start">
            <div>
              <p className="text-[10px] text-green-700 uppercase font-bold flex items-center gap-1">
                <Zap size={12} /> Aggregate Output
              </p>
              <p className="text-2xl font-black text-green-400">
                ~{Math.floor(totalYield).toLocaleString()} Units/Tick
              </p>
            </div>
            <div className="text-right">
              <p className="text-[9px] text-zinc-500 uppercase">Efficiency</p>
              <p className="text-xs font-bold text-white">100%</p>
            </div>
          </div>
          <Zap
            className="absolute -bottom-4 -right-4 text-green-900/20 group-hover:text-green-900/30 transition-all"
            size={80}
          />
        </div>
      </div>

      {/* Control Bar */}
      <div className="flex flex-wrap gap-4 bg-zinc-950 p-3 border border-zinc-800 items-center">
        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <Filter size={14} className="text-zinc-500" />
          <select
            value={rarityFilter}
            onChange={(e) => setRarityFilter(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
          >
            <option value="all" className="bg-black text-white">
              All Tiers
            </option>
            <option value="common" className="bg-black text-white">
              Common
            </option>
            <option value="rare" className="bg-black text-white">
              Rare
            </option>
            <option value="epic" className="bg-black text-white">
              Epic
            </option>
            <option value="legendary" className="bg-black text-white">
              Legendary
            </option>
          </select>
        </div>

        <div className="flex items-center gap-2 bg-black border border-zinc-700 px-3 py-1">
          <ArrowUpDown size={14} className="text-zinc-500" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent text-[10px] text-white outline-none uppercase cursor-pointer"
          >
            <option value="yield_desc" className="bg-black text-white">
              Highest Output
            </option>
            <option value="yield_asc" className="bg-black text-white">
              Lowest Output
            </option>
            <option value="qty" className="bg-black text-white">
              Quantity
            </option>
          </select>
        </div>
      </div>

      {/* Unit Grid */}
      <div className="grid grid-cols-1 gap-3">
        {filteredCooks.length === 0 ? (
          <div className="p-12 text-center border-2 border-dashed border-zinc-800 text-zinc-600 font-mono uppercase text-xs">
            Lab is empty. Recruit cooks to start production.
          </div>
        ) : (
          filteredCooks.map((unit) => (
            <div
              key={unit.id}
              className={`border-l-4 p-4 flex flex-col md:flex-row justify-between items-center gap-4 ${
                RARITY_COLORS[unit.rarity] || RARITY_COLORS.common
              } border-y border-r border-zinc-900 transition-all hover:bg-zinc-900`}
            >
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="bg-black p-3 border border-zinc-800">
                  <FlaskConical
                    size={20}
                    className={
                      unit.rarity === "legendary"
                        ? "text-yellow-500 animate-pulse"
                        : "text-zinc-400"
                    }
                  />
                </div>
                <div>
                  <h4 className="font-black text-white uppercase italic text-sm flex items-center gap-2">
                    {unit.name}
                    <span className="text-[8px] bg-black border border-zinc-800 px-1 py-0.5 rounded text-zinc-500 font-mono">
                      {unit.rarity}
                    </span>
                  </h4>
                  <p className="text-[10px] text-zinc-500 font-mono uppercase mt-1">
                    Staff ID: #{unit.id.toString().padStart(4, "0")} | Qty:{" "}
                    <span className="text-white font-bold">
                      {unit.quantity}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-8 w-full md:w-auto justify-between md:justify-end">
                <div className="text-right">
                  <p className="text-[10px] text-zinc-500 uppercase font-bold">
                    Status
                  </p>
                  <p
                    className={`text-xs font-black uppercase flex items-center gap-1 justify-end ${
                      unit.assigned_recipe_id
                        ? "text-green-500"
                        : "text-zinc-600"
                    }`}
                  >
                    {unit.assigned_recipe_id ? (
                      <CheckCircle2 size={10} />
                    ) : (
                      <AlertCircle size={10} />
                    )}
                    {unit.assigned_recipe_id ? "COOKING" : "IDLE"}
                  </p>
                </div>

                <div className="text-right min-w-[80px]">
                  <p className="text-[10px] text-zinc-500 uppercase font-bold">
                    Output/Unit
                  </p>
                  <p className="text-sm font-black text-white">
                    {unit.production_rate}{" "}
                    <span className="text-[10px] text-zinc-600">
                      units/tick
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
